import React from "react";
import moment from "moment";
import Badge from "@dtci/spellbook/Badge";

/**
 * "Days Till Start" badge for the promo grid.
 *
 * Uses the backend-calculated `daysFromStart` field:
 *   - Negative values = days before start (future)
 *   - Zero = today is start date
 *   - Positive values = days after start (past/active)
 *
 * Display logic:
 *   - Future (daysFromStart < 0):   grey badge, "-X Not Started" (e.g., "-5 Not Started")
 *   - Today (daysFromStart === 0):  blue badge, "+0 Started"
 *   - Started (daysFromStart > 0):  blue/green badge, "+X Started"
 */
export const computeDaysFromStartDisplay = (daysFromStart, endDate, now = new Date()) => {
  if (daysFromStart === null || daysFromStart === undefined) return null;

  const today = moment(now).startOf("day");
  const end = endDate ? moment(endDate).startOf("day") : null;

  // Check if promo window has ended
  const hasEnded = end && today.isAfter(end);

  if (daysFromStart < 0) {
    // Future: starts in the future - show "-X Not Started"
    return {
      state: "future",
      days: daysFromStart,
      label: `${daysFromStart} Not Started`,
      color: "base",
      tooltip: `Starts in ${Math.abs(daysFromStart)} day${Math.abs(daysFromStart) === 1 ? "" : "s"}`,
    };
  }

  // daysFromStart >= 0: has started - show "+X Started"
  return {
    state: hasEnded ? "ended" : "active",
    days: daysFromStart,
    label: `+${daysFromStart} Started`,
    color: hasEnded ? "success" : "active",
    tooltip: daysFromStart === 0
      ? "Started today"
      : `Started ${daysFromStart} day${daysFromStart === 1 ? "" : "s"} ago${hasEnded ? " (promo has ended)" : ""}`,
  };
};

const DaysFromStartCellRenderer = ({ data }) => {
  const result = computeDaysFromStartDisplay(data?.daysFromStart, data?.endDate);
  if (!result) return null;

  return (
    <Badge light color={result.color} title={result.tooltip}>
      {result.label}
    </Badge>
  );
};

export default DaysFromStartCellRenderer;
